import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { KpiTile, PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge, ScoreBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dt, relative, title } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { ExportDialog } from "@/components/lists/ExportDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { SendToCrmDialog } from "@/components/crm/SendToCrmDialog";

const STAGES = ["new", "to_review", "assigned", "contact_attempted", "callback_scheduled",
  "in_conversation", "qualified", "offer_sent", "converted", "lost"];
const STAGE_TONE = {
  new: { bar: "bg-sky-500", head: "bg-sky-50", text: "text-sky-800", count: "bg-sky-600" },
  to_review: { bar: "bg-blue-500", head: "bg-blue-50", text: "text-blue-800", count: "bg-blue-600" },
  assigned: { bar: "bg-indigo-500", head: "bg-indigo-50", text: "text-indigo-800", count: "bg-indigo-600" },
  contact_attempted: { bar: "bg-violet-500", head: "bg-violet-50", text: "text-violet-800", count: "bg-violet-600" },
  callback_scheduled: { bar: "bg-purple-500", head: "bg-purple-50", text: "text-purple-800", count: "bg-purple-600" },
  in_conversation: { bar: "bg-fuchsia-500", head: "bg-fuchsia-50", text: "text-fuchsia-800", count: "bg-fuchsia-600" },
  qualified: { bar: "bg-teal-500", head: "bg-teal-50", text: "text-teal-800", count: "bg-teal-600" },
  offer_sent: { bar: "bg-amber-500", head: "bg-amber-50", text: "text-amber-800", count: "bg-amber-600" },
  converted: { bar: "bg-emerald-500", head: "bg-emerald-50", text: "text-emerald-800", count: "bg-emerald-600" },
  lost: { bar: "bg-rose-500", head: "bg-rose-50", text: "text-rose-800", count: "bg-rose-600" },
};
const INTEREST_TONE = { hot: "bg-rose-500 text-white border-rose-600",
  warm: "bg-amber-500 text-white border-amber-600",
  cold: "bg-sky-500 text-white border-sky-600",
  unqualified: "bg-slate-200 text-slate-700 border-slate-300",
  unknown: "bg-slate-100 text-slate-500 border-slate-200" };

const LeadCard = ({ lead, onDragStart, onOpen }) => (
  <div
    draggable
    onDragStart={(e) => onDragStart(e, lead)}
    onClick={() => onOpen(lead)}
    data-testid={`lead-card-${lead.id}`}
    className="cursor-grab rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md active:cursor-grabbing"
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-800">{lead.contact_name}</p>
        <p className="truncate text-[11px] text-slate-500">{lead.company || lead.phone}</p>
      </div>
      <ScoreBadge score={lead.interest_score} />
    </div>
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${INTEREST_TONE[lead.interest_level || "unknown"]}`}
        data-testid={`lead-interest-${lead.id}`}>
        {title(lead.interest_level || "unknown")}
      </span>
      {lead.lead_category ? (
        <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-600">
          {title(lead.lead_category)}
        </span>
      ) : null}
      {lead.overdue ? (
        <span className="rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700" data-testid={`lead-overdue-${lead.id}`}>
          Overdue
        </span>
      ) : null}
    </div>
    <p className="mt-1 truncate text-[11px] text-slate-500">{lead.product}</p>
    <p className="mt-1 text-[11px] text-slate-400">
      {lead.assigned_to_name ? lead.assigned_to_name : "Unassigned"} · {lead.call_attempts || 1} call(s) ·{" "}
      {(lead.country || "").toUpperCase()}/{(lead.language || "").toUpperCase()}
    </p>
    {lead.next_action ? <p className="mt-1 truncate text-[11px] text-blue-700">Next: {lead.next_action}</p> : null}
    {lead.preferred_callback_at ? <p className="text-[11px] text-slate-500">Callback {dt(lead.preferred_callback_at)}</p> : null}
    <p className="mt-1 text-[11px] text-slate-400">Active {relative(lead.last_activity_at || lead.updated_at)}</p>
  </div>
);

export default function Leads() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [view, setView] = useState("pipeline");
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [dragging, setDragging] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [crmOpen, setCrmOpen] = useState(false);
  const boardRef = useRef(null);

  const boardView = view === "my" ? "my" : view === "needs_attention" ? "needs_attention" : undefined;
  const board = useQuery({
    queryKey: ["leads-board", boardView],
    queryFn: () => get(`/leads/board${boardView ? `?view=${boardView}` : ""}`),
    refetchInterval: 15000,
  });
  const table = useQuery({
    queryKey: ["leads", page, q, stage, boardView],
    queryFn: () => get("/leads", { page, page_size: 25, q: q || undefined,
      pipeline_stage: stage || undefined, view: boardView }),
  });
  const stats = useQuery({ queryKey: ["leads-stats"], queryFn: () => get("/leads-stats") });

  const move = useMutation({
    mutationFn: ({ id, pipeline_stage }) => api.patch(`/leads/${id}`, { pipeline_stage }),
    onSuccess: (_, { pipeline_stage }) => {
      toast.success(`Moved to ${title(pipeline_stage)}`);
      queryClient.invalidateQueries({ queryKey: ["leads-board"] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["leads-stats"] });
    },
    onError: (e) => toast.error(
      e.response?.data?.detail?.message || apiError(e)),
  });

  useEffect(() => {
    const columns = board.data?.board;
    const scroller = boardRef.current;
    if (!columns || !scroller) return;
    const first = STAGES.find((stage) => (columns[stage]?.count || 0) > 0);
    if (!first) return;
    const column = scroller.querySelector(`[data-testid="stage-column-${first}"]`);
    if (column) scroller.scrollTo({ left: column.offsetLeft, behavior: "smooth" });
  }, [board.data]);

  const onDrop = (targetStage) => {
    if (!dragging || dragging.pipeline_stage === targetStage) return;
    move.mutate({ id: dragging.id, pipeline_stage: targetStage });
    setDragging(null);
  };

  return (
    <>
      <PageHeader
        testId="leads-page"
        title="Qualified leads"
        subtitle="Kanban columns are pipeline stages — interest level is a badge, never a column"
        actions={can("exports", "write") ? (
          <Button variant="outline" onClick={() => setExporting(true)} data-testid="leads-export-button">
            Export leads
          </Button>
        ) : null}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <KpiTile testId="leads-kpi-total" label="Total leads" value={stats.data?.total ?? 0} />
        <KpiTile testId="leads-kpi-qualified" label="Qualified" value={(stats.data?.by_stage || []).find((row) => row._id === "qualified")?.count ?? 0} tone="success" />
        <KpiTile testId="leads-kpi-hot" label="Hot" value={stats.data?.hot ?? 0} tone="danger" />
        <KpiTile testId="leads-kpi-unassigned" label="Unassigned" value={stats.data?.unassigned ?? 0} />
        <KpiTile testId="leads-kpi-overdue" label="Overdue callbacks" value={stats.data?.overdue ?? 0} tone="danger" />
        <KpiTile testId="leads-kpi-converted" label="Converted" value={stats.data?.converted ?? 0} tone="success" />
        <KpiTile testId="leads-kpi-conversion" label="Conversion rate" value={`${stats.data?.conversion_rate ?? 0}%`} tone="success" />
      </div>

      <Tabs value={view} onValueChange={(v) => { setView(v); setPage(1); setSelected([]); }}>
        <TabsList className="flex-wrap bg-slate-100">
          <TabsTrigger value="pipeline" data-testid="leads-view-pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="all" data-testid="leads-view-all">All leads</TabsTrigger>
          <TabsTrigger value="my" data-testid="leads-view-my">My leads</TabsTrigger>
          <TabsTrigger value="needs_attention" data-testid="leads-view-attention">Needs attention</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="mt-4">
          <div ref={boardRef} className="relative flex flex-nowrap gap-3 overflow-x-auto pb-4" data-testid="leads-kanban">
            {STAGES.map((column) => (
              <div
                key={column}
                className="w-64 shrink-0"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(column)}
                data-testid={`stage-column-${column}`}
              >
                <div className="panel overflow-hidden">
                  <div className={cn("h-1", STAGE_TONE[column]?.bar || "bg-slate-300")} />
                  <div className={cn("flex items-center justify-between border-b border-slate-200 px-3 py-2", STAGE_TONE[column]?.head)}>
                    <span className={cn("text-[11px] font-bold uppercase tracking-wider", STAGE_TONE[column]?.text || "text-slate-700")}>{title(column)}</span>
                    <span className={cn("rounded-full px-2 py-0.5 text-xs font-bold text-white", STAGE_TONE[column]?.count || "bg-slate-500")} data-testid={`stage-count-${column}`}>
                      {board.data?.board?.[column]?.count ?? 0}
                    </span>
                  </div>
                  <div className="max-h-[34rem] space-y-2 overflow-y-auto p-2">
                    {(board.data?.board?.[column]?.items || []).length === 0 ? (
                      <p className="px-1 py-3 text-xs text-slate-400">No leads</p>
                    ) : board.data.board[column].items.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onDragStart={(e, l) => { setDragging(l); e.dataTransfer.effectAllowed = "move"; }}
                        onOpen={(l) => navigate(`/leads/${l.id}`)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">Drag a card between stages — every move is written to stage history, the activity timeline and the audit log.</p>
        </TabsContent>

        {["all", "my", "needs_attention"].map((tab) => {
          const rows = table.data?.items || [];
          const selectable = rows.filter((row) => row.pipeline_stage === "qualified");
          const pageSelected = selectable.filter((row) => selected.includes(row.id));
          const allSelected = selectable.length > 0 && pageSelected.length === selectable.length;
          return (
          <TabsContent key={tab} value={tab} className="mt-4">
            {selected.length && can("leads", "write") ? (
              <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2" data-testid="leads-bulk-bar">
                <span className="text-sm font-medium text-blue-900">{selected.length} qualified selected</span>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setCrmOpen(true)} data-testid="leads-send-crm">
                  Send to CRM
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
              </div>
            ) : null}
            <Section
              testId={`leads-table-section-${tab}`}
              title={`${table.data?.total ?? 0} leads`}
              subtitle="Only qualified leads can be selected and sent to a CRM"
              actions={
                <div className="flex gap-2">
                  <Input data-testid="leads-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, phone, company" className="h-9 w-56" />
                  <Select value={stage || "all"} onValueChange={(v) => { setStage(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger data-testid="leads-stage-filter" className="h-9 w-44"><SelectValue placeholder="Pipeline stage" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All stages</SelectItem>
                      {STAGES.map((s) => <SelectItem key={s} value={s}>{title(s)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              }
            >
              <DataTable
                testId="leads-table"
                rowTestId="lead-row"
                loading={table.isLoading}
                onRowClick={(r) => navigate(`/leads/${r.id}`)}
                columns={[
                  ...(can("leads", "write") ? [{
                    key: "select",
                    label: (
                      <Checkbox
                        data-testid="leads-select-all"
                        checked={allSelected ? true : pageSelected.length ? "indeterminate" : false}
                        disabled={!selectable.length}
                        onCheckedChange={(checked) => {
                          const ids = selectable.map((row) => row.id);
                          setSelected((current) => (checked
                            ? Array.from(new Set([...current, ...ids]))
                            : current.filter((id) => !ids.includes(id))));
                        }}
                      />
                    ),
                    width: "36px",
                    render: (r) => {
                      const qualified = r.pipeline_stage === "qualified";
                      return (
                        <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                          <Checkbox
                            data-testid={`lead-select-${r.id}`}
                            disabled={!qualified}
                            checked={qualified && selected.includes(r.id)}
                            onCheckedChange={(checked) => setSelected((current) => {
                              if (checked) return current.includes(r.id) ? current : [...current, r.id];
                              return current.filter((id) => id !== r.id);
                            })}
                          />
                        </span>
                      );
                    },
                  }] : []),
                  { key: "contact_name", label: "Contact", render: (r) => <span className="font-medium text-slate-800">{r.contact_name}</span> },
                  { key: "company", label: "Company", render: (r) => r.company || "—" },
                  { key: "phone", label: "Phone", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
                  { key: "product", label: "Product" },
                  { key: "interest_level", label: "Interest", render: (r) => title(r.interest_level || "unknown") },
                  { key: "interest_score", label: "Score", render: (r) => <ScoreBadge score={r.interest_score} /> },
                  { key: "lead_category", label: "Category", render: (r) => (r.lead_category ? title(r.lead_category) : "—") },
                  { key: "pipeline_stage", label: "Stage", render: (r) => <StatusBadge value="running" label={title(r.pipeline_stage || "new")} testId={`lead-stage-${r.id}`} /> },
                  { key: "crm", label: "CRM", render: (r) => {
                    const status = r.crm_sync?.status;
                    if (status === "sent") return <span className="font-medium text-emerald-700" data-testid={`lead-crm-${r.id}`}>Sent</span>;
                    if (status === "failed") return <span className="font-medium text-rose-700" data-testid={`lead-crm-${r.id}`}>Failed</span>;
                    return <span className="text-slate-400">—</span>;
                  } },
                  { key: "assigned_to_name", label: "Assigned", render: (r) => r.assigned_to_name || "—" },
                  { key: "preferred_callback_at", label: "Callback", render: (r) => (r.preferred_callback_at ? dt(r.preferred_callback_at) : "—") },
                  { key: "last_activity_at", label: "Last activity", render: (r) => relative(r.last_activity_at || r.updated_at) },
                  { key: "actions", label: "", render: (r) => (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" asChild data-testid={`lead-open-${r.id}`}>
                        <Link to={`/leads/${r.id}`}>Open</Link>
                      </Button>
                    </div>
                  ) },
                ]}
                rows={rows}
                empty={{ title: "No leads here", description: "Leads are created automatically from qualifying call outcomes." }}
              />
              <Pagination page={table.data?.page || 1} pages={table.data?.pages || 1} total={table.data?.total || 0} onChange={setPage} />
            </Section>
          </TabsContent>
          );
        })}
      </Tabs>

      <ExportDialog open={exporting} onClose={() => setExporting(false)} scopeType="leads" scopeName="Qualified leads" />
      <SendToCrmDialog
        open={crmOpen}
        onClose={() => setCrmOpen(false)}
        leadIds={selected}
        onSent={(data) => {
          if (!data?.failed) setSelected([]);
          queryClient.invalidateQueries({ queryKey: ["leads"] });
          queryClient.invalidateQueries({ queryKey: ["leads-board"] });
        }}
      />
    </>
  );
}